/**
 * CelebrationOverlay - Phase 4
 * 
 * Displays celebratory animations for achievements.
 * Uses canvas-confetti for particle effects.
 * 
 * @module CelebrationOverlay
 */

import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useGamification } from '../../context/GamificationContext';

// ============================================================================
// CONFETTI CONFIGURATIONS
// ============================================================================

const confettiConfigs = {
  // Level up - big explosion
  levelUp: () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 9999,
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  },

  // Badge earned - stars
  badge: () => {
    const defaults = {
      spread: 360,
      ticks: 100,
      gravity: 0,
      decay: 0.94,
      startVelocity: 30,
      shapes: ['star'],
      colors: ['#FFD700', '#FFA500', '#FF6347', '#FFE066'],
      zIndex: 9999,
    };

    confetti({
      ...defaults,
      particleCount: 40,
      scalar: 1.2,
      shapes: ['star'],
    });

    confetti({
      ...defaults,
      particleCount: 20,
      scalar: 0.75,
      shapes: ['circle'],
    });
  },

  // Streak milestone - fire colors
  streakMilestone: () => {
    const end = Date.now() + 1500;
    const colors = ['#FF4500', '#FF6B35', '#FFD700', '#FF8C00'];

    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors,
        zIndex: 9999,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors,
        zIndex: 9999,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  },

  // Task complete - subtle burst
  taskComplete: () => {
    confetti({
      particleCount: 30,
      spread: 50,
      origin: { y: 0.8 },
      colors: ['#22c55e', '#4ade80', '#86efac'],
      zIndex: 9999,
      scalar: 0.8,
    });
  },

  // XP gain - coins
  xpGain: () => {
    confetti({
      particleCount: 20,
      spread: 40,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFC107', '#FFEB3B'],
      shapes: ['circle'],
      zIndex: 9999,
      scalar: 0.6,
      gravity: 1.5,
    });
  },
};

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9998,
    pointerEvents: 'none',
  },
  card: {
    background: 'white',
    borderRadius: '24px',
    padding: '32px 48px',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    maxWidth: '320px',
    pointerEvents: 'auto',
  },
  icon: {
    fontSize: '64px',
    marginBottom: '16px',
    display: 'block',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '16px',
  },
  highlight: {
    display: 'inline-block',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
  },
  badgeList: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  badgeItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px',
    background: '#fef3c7',
    borderRadius: '12px',
    minWidth: '80px',
  },
  dismissButton: {
    marginTop: '24px',
    padding: '12px 32px',
    background: 'transparent',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    color: '#6b7280',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

// ============================================================================
// CELEBRATION CARD COMPONENTS
// ============================================================================

const LevelUpCard = ({ oldLevel, newLevel, onDismiss }) => (
  <motion.div
    style={styles.card}
    initial={{ scale: 0.5, opacity: 0, y: 50 }}
    animate={{ scale: 1, opacity: 1, y: 0 }}
    exit={{ scale: 0.5, opacity: 0, y: -50 }}
    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
  >
    <motion.span
      style={styles.icon}
      animate={{ 
        rotate: [0, -15, 15, -15, 15, 0],
        scale: [1, 1.2, 1],
      }}
      transition={{ duration: 0.6 }}
    >
      🎉
    </motion.span>
    <h2 style={styles.title}>Level Up!</h2>
    <p style={styles.subtitle}>
      You reached <strong>Level {newLevel.level}</strong>
    </p>
    <motion.div
      style={styles.highlight}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.3 }}
    >
      {newLevel.title}
    </motion.div>
    <button style={styles.dismissButton} onClick={onDismiss}>
      Continue
    </button>
  </motion.div>
);

const BadgeCard = ({ badges, onDismiss }) => (
  <motion.div
    style={styles.card}
    initial={{ scale: 0.5, opacity: 0, y: 50 }}
    animate={{ scale: 1, opacity: 1, y: 0 }}
    exit={{ scale: 0.5, opacity: 0, y: -50 }}
    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
  >
    <motion.span
      style={styles.icon}
      animate={{ scale: [1, 1.3, 1] }}
      transition={{ duration: 0.5 }}
    >
      🏆
    </motion.span>
    <h2 style={styles.title}>Badge Earned!</h2>
    <p style={styles.subtitle}>
      {badges.length === 1 
        ? 'You unlocked a new badge' 
        : `You unlocked ${badges.length} new badges`}
    </p>
    <div style={styles.badgeList}>
      {badges.map((badge, index) => (
        <motion.div
          key={badge.id}
          style={styles.badgeItem}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: index * 0.1 + 0.3 }}
        >
          <span style={{ fontSize: '28px' }}>{badge.icon}</span>
          <span style={{ fontSize: '11px', fontWeight: '600', marginTop: '4px' }}>
            {badge.name}
          </span>
        </motion.div>
      ))}
    </div>
    <button style={styles.dismissButton} onClick={onDismiss}>
      Awesome!
    </button>
  </motion.div>
);

const StreakMilestoneCard = ({ streak, onDismiss }) => (
  <motion.div
    style={styles.card}
    initial={{ scale: 0.5, opacity: 0, y: 50 }}
    animate={{ scale: 1, opacity: 1, y: 0 }}
    exit={{ scale: 0.5, opacity: 0, y: -50 }}
    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
  >
    <motion.span
      style={styles.icon}
      animate={{ 
        scale: [1, 1.2, 1],
        filter: ['brightness(1)', 'brightness(1.5)', 'brightness(1)'],
      }}
      transition={{ duration: 0.5, repeat: 2 }}
    >
      🔥
    </motion.span>
    <h2 style={styles.title}>{streak} Day Streak!</h2>
    <p style={styles.subtitle}>
      You're on fire! Keep up the momentum.
    </p>
    <motion.div
      style={{
        ...styles.highlight,
        background: 'linear-gradient(135deg, #FF4500 0%, #FF6B35 100%)',
      }}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.3 }}
    >
      🎯 {streak / 7} Week{streak / 7 > 1 ? 's' : ''} Strong
    </motion.div>
    <button style={styles.dismissButton} onClick={onDismiss}>
      Let's Go!
    </button>
  </motion.div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CelebrationOverlay = () => {
  const { celebration, showCelebration, dismissCelebration } = useGamification();

  // Trigger confetti based on celebration type
  useEffect(() => {
    if (showCelebration && celebration) {
      switch (celebration.type) {
        case 'levelUp':
          confettiConfigs.levelUp();
          break;
        case 'badge':
          confettiConfigs.badge();
          break;
        case 'streakMilestone':
          confettiConfigs.streakMilestone();
          break;
        case 'taskComplete':
          confettiConfigs.taskComplete();
          break;
        case 'xpGain':
          confettiConfigs.xpGain();
          break;
        default:
          break;
      }
    }
  }, [showCelebration, celebration]);

  const renderCard = useCallback(() => {
    if (!celebration) return null;

    switch (celebration.type) {
      case 'levelUp':
        return (
          <LevelUpCard
            oldLevel={celebration.data?.oldLevel}
            newLevel={celebration.data?.newLevel}
            onDismiss={dismissCelebration}
          />
        );
      case 'badge':
        return (
          <BadgeCard
            badges={celebration.data?.badges || []}
            onDismiss={dismissCelebration}
          />
        );
      case 'streakMilestone':
        return (
          <StreakMilestoneCard
            streak={celebration.data?.streak}
            onDismiss={dismissCelebration}
          />
        );
      default:
        return null;
    }
  }, [celebration, dismissCelebration]);

  return (
    <AnimatePresence>
      {showCelebration && celebration && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {renderCard()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// STANDALONE CONFETTI TRIGGERS
// ============================================================================

/**
 * Trigger confetti manually (for use outside context)
 */
export const triggerConfetti = (type = 'taskComplete') => {
  const config = confettiConfigs[type];
  if (config) {
    config();
  }
};

/**
 * Trigger XP gain animation at a specific element
 */
export const triggerXPBurst = (element) => {
  if (!element) return;
  
  const rect = element.getBoundingClientRect();
  const x = (rect.left + rect.width / 2) / window.innerWidth;
  const y = (rect.top + rect.height / 2) / window.innerHeight;

  confetti({
    particleCount: 15,
    spread: 30,
    origin: { x, y },
    colors: ['#FFD700', '#FFC107'],
    shapes: ['circle'],
    scalar: 0.5,
    gravity: 1.2,
    zIndex: 9999,
  });
};

/**
 * Celebration for completing all daily tasks
 */
export const triggerAllTasksComplete = () => {
  const duration = 2000;
  const end = Date.now() + duration;

  (function frame() {
    confetti({
      particleCount: 5,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#22c55e', '#4ade80', '#86efac', '#bbf7d0'],
      zIndex: 9999,
    });
    confetti({
      particleCount: 5,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#22c55e', '#4ade80', '#86efac', '#bbf7d0'],
      zIndex: 9999,
    });

    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  })();
};

export default CelebrationOverlay;
